import { Link, useParams } from '@tanstack/react-router';
import { Eye, Lock, Unlock, X } from 'lucide-react';
import { useState } from 'react';

import { PortalSummaryCard, type PortalCardView } from '@/components/PortalSummaryCard';
import { Button } from '@/components/ui/button';

/**
 * Practitioner-only "preview the customer portal" route.
 *
 * Renders the same `PortalSummaryCard` the customer would see for this
 * customer, with two view toggles up top:
 *
 * - **Prospect (locked)** — what a customer who hasn't signed the
 *   engagement sees: layout, copy, and trust signals, but every $ + count
 *   is blurred. CRITICAL: this view does NOT call `/api/portal/summary`;
 *   prospect mode is a tease, and a curious customer should not be able
 *   to inspect their would-be numbers via DevTools.
 * - **Signed client** — the full report card with real numbers; passes
 *   `?as_customer=<slug>` on the API call. The backend's
 *   `_require_portal_user` validates that the caller is a practitioner
 *   before honoring the param.
 *
 * The amber banner makes "this is not your real portal" obvious so
 * practitioners can't accidentally show a screenshot of preview chrome
 * to a customer and confuse them.
 */
export function CustomerPortalPreviewPage() {
  const { slug } = useParams({ from: '/customers/$slug/preview' });
  const [view, setView] = useState<PortalCardView>('signed');

  return (
    <div className="grid gap-6">
      <PreviewBanner slug={slug} />
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-brand-navy">Recovery portal</h1>
        <p className="mt-1 text-sm text-brand-slate">
          Toggle between the prospect (locked) view a not-yet-signed customer sees and the full
          report a signed customer sees.
        </p>
      </div>
      <ViewToggle view={view} onChange={setView} />
      <PortalSummaryCard previewSlug={slug} view={view} />
    </div>
  );
}

function ViewToggle({
  view,
  onChange,
}: {
  view: PortalCardView;
  onChange: (next: PortalCardView) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs uppercase tracking-wider text-brand-slate">Preview as</span>
      <Button
        type="button"
        size="sm"
        variant={view === 'prospect' ? 'default' : 'outline'}
        onClick={() => onChange('prospect')}
      >
        <Lock className="mr-1.5 h-3.5 w-3.5" aria-hidden />
        Prospect (locked)
      </Button>
      <Button
        type="button"
        size="sm"
        variant={view === 'signed' ? 'default' : 'outline'}
        onClick={() => onChange('signed')}
      >
        <Unlock className="mr-1.5 h-3.5 w-3.5" aria-hidden />
        Signed client
      </Button>
    </div>
  );
}

function PreviewBanner({ slug }: { slug: string }) {
  return (
    <div className="sticky top-0 z-20 -mx-4 flex items-center justify-between gap-3 border-b border-amber-300 bg-amber-100/90 px-4 py-2 text-sm text-amber-900 shadow-sm backdrop-blur sm:-mx-6 sm:px-6">
      <div className="flex items-center gap-2">
        <Eye className="h-4 w-4 shrink-0" aria-hidden />
        <span>
          Preview mode — viewing the customer portal as <strong>{slug}</strong>. This is what the
          customer sees.
        </span>
      </div>
      <Button asChild variant="outline" size="sm" className="shrink-0 bg-white">
        <Link to="/customers/$slug" params={{ slug }}>
          <X className="mr-1 h-3.5 w-3.5" aria-hidden />
          Exit preview
        </Link>
      </Button>
    </div>
  );
}
